import ProjectDetailPage from './components/projects/ProjectDetailPage';
import { supportTaskTarget, SupportTaskTarget } from './services/supportTaskNavigation';
import { clearRetiredIntegrationSettings } from './services/retiredIntegrationCleanup';
import MfaBoundary from './components/security/MfaBoundary';
import { usePermissions } from './hooks/usePermissions';
import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { logger } from './utils/logger';
import LoginForm from './components/LoginForm';
import { PublicProposalV2 } from './components/proposals/public/PublicProposalV2';
import { ContractViewer } from './components/contracts/ContractViewer';
import { MaintenancePortal } from './components/maintenance/MaintenancePortal';
import authService from './services/authService';
import { supabase } from './services/supabaseService';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import ContactList from './components/ContactList';
import ActivityList from './components/ActivityList';
import MeetingList from './components/MeetingList';
import InternalCalendar from './components/InternalCalendar';
import CalendlyModule from './components/CalendlyModule';
import UserManagement from './components/UserManagement';
import Settings from './components/Settings';
import CollaboratorsModule from './components/CollaboratorsModule';
import NotificationsModule from './components/NotificationsModule';
import UserProfile from './components/UserProfile';
import LeadsList from './components/LeadsList';
import ArchivedLeadsList from './components/ArchivedLeadsList';
import DiscardedLeadsList from './components/DiscardedLeadsList';
// PublicAgenda removed — /agenda now redirects to ceroideas.es/contacto
import FathomModule from './components/FathomModule';
import ResetPasswordPage from './components/ResetPasswordPage';
import AcceptInvitationPage from './components/AcceptInvitationPage';
import ConnectionErrorAlert from './components/ConnectionErrorAlert';
import WhatsAppSupport from './components/WhatsAppSupport';
import MonitoringDashboard from './components/MonitoringDashboard';
import ActivityHistory from './components/ActivityHistory';
import SystemControlCenter from './components/SystemControlCenter';
import { configService } from './services/configService';
import { connectionMonitor } from './services/connectionMonitor';
import { realtimeSyncService } from './services/realtimeSyncService';
import { DataSyncProvider } from './contexts/DataSyncContext';
import { DataCacheProvider } from './contexts/DataCacheContext';
import { versionManager } from './utils/versionManager';
import { sessionIntegrityService } from './services/sessionIntegrityService';
import { structuredLogger } from './services/structuredLogger';
import { budgetSyncService } from './services/budgetSyncService';
import ProjectsModule from './components/projects/ProjectsModule';
import ProposalsModule from './components/proposals/ProposalsModule';
import MaintenanceModule from './components/maintenance/MaintenanceModule';
import MyMaintenances from './components/maintenance/MyMaintenances';
import CommercialDashboard from './components/CommercialDashboard';
import OutboundCallQueue from './components/OutboundCallQueue';

function SuperadminOnly({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin, loading } = usePermissions();
  if (loading) return <p role="status">Comprobando acceso…</p>;
  return isSuperAdmin() ? <>{children}</> : <p role="alert">Esta sección está reservada al superadministrador.</p>;
}

function App() {
  const location = useLocation();

  // Public routes — render independently, no auth needed
  if (location.pathname.startsWith('/propuestas/')) {
    return <Routes><Route path="/propuestas/:slug" element={<PublicProposalV2 />} /></Routes>;
  }
  if (location.pathname.startsWith('/contrato/')) {
    return <Routes><Route path="/contrato/:token" element={<ContractViewer />} /></Routes>;
  }
  if (location.pathname.startsWith('/portal/mantenimiento/')) {
    return <Routes><Route path="/portal/mantenimiento/:token" element={<MaintenancePortal />} /></Routes>;
  }

  return <CRMApp />;
}

function CRMApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [supportTarget,setSupportTarget]=useState<SupportTaskTarget | null>(null);
  useEffect(()=>{
    if(!isAuthenticated) return;
    const open=(event:Event)=>{const d=(event as CustomEvent).detail;const t=supportTaskTarget(d?.projectId,d?.taskId);if(t){setSupportTarget(t);setActiveTab('soporte-tarea');}};
    window.addEventListener('navigate-to-support-task',open);
    const q=new URLSearchParams(window.location.search);const t=supportTaskTarget(q.get('support_project'),q.get('support_task'));
    if(t){setSupportTarget(t);setActiveTab('soporte-tarea');}
    return ()=>window.removeEventListener('navigate-to-support-task',open);
  },[isAuthenticated]);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isAcceptInvitation, setIsAcceptInvitation] = useState(false);
  const [isValidatingSession, setIsValidatingSession] = useState(false);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  
  useEffect(() => {
    clearRetiredIntegrationSettings(localStorage);
    clearRetiredIntegrationSettings(sessionStorage);
    initializeApp();
    checkForPasswordReset();
    checkForAcceptInvitation();

    // connectionMonitor + realtimeSyncService now start in a separate effect
    // gated by isAuthenticated to avoid 401 (integration_health) and
    // CHANNEL_ERROR (realtime channels) before login.

    // DISABLED: Budget sync auto-sync - Using webhook-only approach to prevent duplicate contacts
    // The webhook (budget-approval-webhook) handles budget approvals in real-time
    // Auto-sync was causing duplicate contact creation when processing the same budget multiple times
    if (budgetSyncService.isConfigured()) {
      // budgetSyncService.startAutoSync(); // DISABLED - Use manual sync only if needed
      structuredLogger.info('Budget sync service configured (auto-sync disabled - using webhook only)');
    } else {
      structuredLogger.warn('Budget sync service not configured');
    }

    // Listen for navigation events from notifications
    const handleNavigateToTab = (event: CustomEvent) => {
      const { tab, notificationCode } = event.detail;
      console.log('🔗 Navegación desde notificación:', tab, notificationCode);
      setActiveTab(tab);
    };

    // Listen for session integrity violations — surface as a non-blocking
    // banner instead of alert(), which freezes the tab on mobile and blocks
    // the reload path if the app is mid-transition.
    const handleSessionIntegrityViolation = async (event: CustomEvent) => {
      console.error('Violación de integridad de sesión detectada:', event.detail);
      setSessionNotice('Se detectó un problema con tu sesión. Por favor, inicia sesión nuevamente.');
      await authService.logout();
      setIsAuthenticated(false);
      setActiveTab('dashboard');
    };

    const handleCriticalSessionFailure = async (event: CustomEvent) => {
      console.error('Fallo crítico de sesión:', event.detail);
      setSessionNotice('Tu sesión ha sido comprometida por seguridad. Por favor, inicia sesión nuevamente.');
      setIsAuthenticated(false);
      setActiveTab('dashboard');
    };

    window.addEventListener('navigate-to-tab', handleNavigateToTab as EventListener);
    window.addEventListener('session-integrity-violation', handleSessionIntegrityViolation as EventListener);
    window.addEventListener('session-integrity-critical-failure', handleCriticalSessionFailure as EventListener);

    // Cleanup on unmount
    return () => {
      budgetSyncService.stopAutoSync();
      window.removeEventListener('navigate-to-tab', handleNavigateToTab as EventListener);
      window.removeEventListener('session-integrity-violation', handleSessionIntegrityViolation as EventListener);
      window.removeEventListener('session-integrity-critical-failure', handleCriticalSessionFailure as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    connectionMonitor.startMonitoring();
    realtimeSyncService.startMonitoring(['contacts', 'leads', 'meetings', 'activities', 'users']);

    return () => {
      connectionMonitor.stopMonitoring();
      realtimeSyncService.stopMonitoring();
    };
  }, [isAuthenticated]);

  const checkForPasswordReset = () => {
    // Legacy implicit-flow recovery links land as `#type=recovery&access_token=...`.
    // PKCE recovery links (Supabase v2 default) land as `?code=...` with the
    // recovery type only surfaced via the PASSWORD_RECOVERY auth event once
    // the code is exchanged. We accept either shape so switching flowType in
    // the Supabase client doesn't break existing outstanding links.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (hashParams.get('type') === 'recovery' && hashParams.get('access_token')) {
      logger.debug('Recovery link (implicit flow) detectado');
      setIsPasswordReset(true);
      setIsLoading(false);
      return true;
    }

    const searchParams = new URLSearchParams(window.location.search);
    // A bare ?code= that is NOT a Google Calendar callback (which carries
    // ?google_calendar=…) is a Supabase PKCE code — treat as recovery. The
    // ResetPasswordPage waits for the code exchange to complete.
    if (searchParams.get('code') && !searchParams.get('google_calendar')) {
      logger.debug('Recovery link (PKCE) detectado');
      setIsPasswordReset(true);
      setIsLoading(false);
      return true;
    }

    return false;
  };

  const checkForAcceptInvitation = () => {
    const path = window.location.pathname;
    // Aceptamos ambos formatos:
    //   /accept-invitation?token=XXX  (histórico)
    //   /accept-invitation/XXX        (el que usa el email actual)
    const params = new URLSearchParams(window.location.search);
    const queryToken = params.get('token');
    const pathToken = path.startsWith('/accept-invitation/')
      ? path.slice('/accept-invitation/'.length).split(/[/?#]/)[0]
      : null;
    const token = queryToken || pathToken;

    if ((path === '/accept-invitation' || path.startsWith('/accept-invitation/')) && token) {
      console.log('📧 Detectado token de invitación');
      setIsAcceptInvitation(true);
      setIsLoading(false);
      return true;
    }
    return false;
  };

  const initializeApp = async () => {
    if (window.location.pathname === '/agenda' || window.location.pathname === '/agenda.html') {
      // REDIRECT: Public agenda disabled - redirect to contact page
      window.location.replace('https://ceroideas.es/contacto');
      return;
    }

    if (checkForPasswordReset()) {
      return;
    }

    if (checkForAcceptInvitation()) {
      return;
    }

    try {
      logger.info('Inicializando aplicación...');

      // Check for version change (deployment detection)
      const currentVersion = versionManager.getCurrentVersion();
      const previousVersion = versionManager.getPreviousVersion();

      if (previousVersion && previousVersion !== currentVersion) {
        console.log('🔄 Nueva versión detectada:', previousVersion, '→', currentVersion);
        setIsValidatingSession(true);

        // Version changed - validate session integrity
        const fingerprint = sessionIntegrityService.getSessionFingerprint();
        if (fingerprint) {
          logger.debug('Validando sesión después de despliegue...');

          // Log deployment detection (non-blocking)
          structuredLogger.logDeploymentDetected(
            fingerprint.userId,
            fingerprint.email,
            previousVersion,
            currentVersion
          ).catch(err => console.warn('Error logging deployment:', err));
        }
      }

      // Setup auth state listener BEFORE initialize so no events are missed
      authService.onAuthStateChange((session) => {
        setIsAuthenticated(!!session);
        if (!session) {
          setActiveTab('dashboard');
        }
      });

      // Supabase fires PASSWORD_RECOVERY once the recovery link is exchanged
      // (both in PKCE and implicit flows). Route to ResetPasswordPage even if
      // the URL heuristics in checkForPasswordReset missed it.
      supabase.auth.onAuthStateChange((event) => {
        if (event === 'PASSWORD_RECOVERY') {
          logger.info('PASSWORD_RECOVERY event → mostrando ResetPasswordPage');
          setIsPasswordReset(true);
          setIsLoading(false);
        }
      });

      // Initialize auth and WAIT for it before hiding the loading screen
      try {
        await authService.initialize();
        const session = authService.getCurrentSession();
        setIsAuthenticated(!!session);
        if (session) {
          logger.info('Sesión existente restaurada');
        }
      } catch (err: any) {
        console.warn('⚠️ No se pudo restaurar sesión previa:', err.message);
        setIsAuthenticated(false);
      }

      logger.info('Aplicación inicializada');
      setIsLoading(false);
      setIsValidatingSession(false);
    } catch (error) {
      logger.error('Error inicializando aplicación', error);
      setIsAuthenticated(false);
      setIsLoading(false);
      setIsValidatingSession(false);
    }
  };

  const handleLogin = async (email: string, password: string): Promise<boolean> => {
    try {
      const session = await authService.login(email, password);
      setIsAuthenticated(!!session);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setActiveTab('dashboard');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if there's an error
      setIsAuthenticated(false);
      setActiveTab('dashboard');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          {isValidatingSession && (
            <p className="text-sm text-gray-600">Validando sesión después de actualización...</p>
          )}
        </div>
      </div>
    );
  }

  // Render password reset page if recovery token detected
  if (isPasswordReset) {
    return <ResetPasswordPage />;
  }

  // Render accept invitation page if invitation token detected
  if (isAcceptInvitation) {
    return <AcceptInvitationPage />;
  }

  if (!isAuthenticated) {
    return (
      <>
        {sessionNotice && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)] bg-red-50 border border-red-200 text-red-800 rounded-lg shadow-lg px-4 py-3 flex items-start gap-3">
            <span className="flex-1 text-sm">{sessionNotice}</span>
            <button
              onClick={() => setSessionNotice(null)}
              className="text-red-600 hover:text-red-800 font-medium text-sm"
              aria-label="Cerrar aviso"
            >
              ×
            </button>
          </div>
        )}
        <LoginForm onLogin={handleLogin} />
      </>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'leads':
        return <SuperadminOnly><LeadsList /></SuperadminOnly>;
      case 'leads-descartados':
        return <SuperadminOnly><DiscardedLeadsList /></SuperadminOnly>;
      case 'leads-archivados':
        return <SuperadminOnly><ArchivedLeadsList /></SuperadminOnly>;
      case 'contactos':
      case 'empresas':
        // 'empresas' redirige al mismo listado unificado: la tab dedicada
        // se retiró 2026-09-18 y las empresas viven ahora como filas
        // entity_type='company' en contacts.
        return <SuperadminOnly><ContactList /></SuperadminOnly>;
      case 'actividades':
        return <SuperadminOnly><ActivityList /></SuperadminOnly>;
      case 'reuniones':
        return <InternalCalendar />;
      case 'calendly':
        return <SuperadminOnly><CalendlyModule /></SuperadminOnly>;
      case 'presupuestos':
        return <SuperadminOnly><ProposalsModule /></SuperadminOnly>;
      case 'gestion-proyectos':
        return <ProjectsModule />;
      case 'mis-mantenimientos':
        return <MyMaintenances />;
      case 'mantenimiento':
        return <SuperadminOnly><MaintenanceModule /></SuperadminOnly>;
      case 'usuarios':
        return <SuperadminOnly><UserManagement /></SuperadminOnly>;
      case 'pagos':
        return <CollaboratorsModule />;
      case 'fathom':
        return <SuperadminOnly><FathomModule /></SuperadminOnly>;
      case 'comercial':
        return <SuperadminOnly><CommercialDashboard /></SuperadminOnly>;
      case 'prospeccion-llamadas':
        return <SuperadminOnly><OutboundCallQueue /></SuperadminOnly>;
      case 'notificaciones':
        return <NotificationsModule />;
      case 'mi-perfil':
        return <UserProfile />;
      case 'configuracion':
        return <SuperadminOnly><Settings /></SuperadminOnly>;
      case 'control-operativo':
        return <SuperadminOnly><SystemControlCenter /></SuperadminOnly>;
      case 'historial-actividad':
        return <SuperadminOnly><ActivityHistory /></SuperadminOnly>;
      case 'soporte-tarea':
        return supportTarget ? <ProjectDetailPage key={supportTarget.projectId + supportTarget.taskId} projectId={supportTarget.projectId} initialTaskId={supportTarget.taskId} onBack={()=>setActiveTab('gestion-proyectos')} /> : <ProjectsModule />;
      case 'whatsapp-soporte':
        return <SuperadminOnly><WhatsAppSupport /></SuperadminOnly>;
      case 'monitoreo':
        return <SuperadminOnly><MonitoringDashboard /></SuperadminOnly>;
      default:
        return <Dashboard />;
    }
  };

  return (
    <>
      <ConnectionErrorAlert supabaseUrl={import.meta.env.VITE_SUPABASE_URL} />
      <MfaBoundary onLogout={handleLogout}>
      <DataCacheProvider>
        <DataSyncProvider>
          <Layout activeTab={activeTab} onTabChange={setActiveTab} onLogout={handleLogout}>
            {renderContent()}
          </Layout>
        </DataSyncProvider>
      </DataCacheProvider>
      </MfaBoundary>
    </>
  );
}

export default App;